import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/lib/http';
import { pagina } from '@/lib/paginacao';
import {
  chavePendenteSchema,
  empresaResumoSchema,
  loteSchema,
  paginaNumeradaSchema,
  resumoDocumentosSchema,
  resumoPendenciasSchema,
} from '@fiscoai/contracts';

/** Primeiro e último dia do mês corrente, como datas civis. */
export function mesAtual(hoje = new Date()): { de: string; ate: string } {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  const ultimo = new Date(ano, mes + 1, 0).getDate();
  return {
    de: `${String(ano)}-${doisDigitos(mes + 1)}-01`,
    ate: `${String(ano)}-${doisDigitos(mes + 1)}-${doisDigitos(ultimo)}`,
  };
}

export function useResumoDoMes(empresaIds: readonly string[]) {
  const { de, ate } = mesAtual();
  return useQuery({
    queryKey: ['inicio', 'resumo', empresaIds, de],
    queryFn: ({ signal }) =>
      request('/documentos/resumo', {
        schema: resumoDocumentosSchema,
        query: { empresaIds, emitidaDe: de, emitidaAte: ate },
        signal,
      }),
  });
}

/** Os 5 envios mais recentes (o histórico usa a página numerada; aqui só a primeira, de 10). */
export function useUltimosLotes() {
  return useQuery({
    queryKey: ['inicio', 'lotes'],
    queryFn: async ({ signal }) => {
      const pagina = await request('/importacoes', {
        schema: paginaNumeradaSchema(loteSchema),
        query: { pagina: 1, porPagina: 10, ordenarPor: 'criadoEm', ordem: 'desc' },
        signal,
      });
      return { ...pagina, items: pagina.items.slice(0, 5) };
    },
  });
}

export function useChavesRecentes(empresaIds: readonly string[]) {
  return useQuery({
    queryKey: ['inicio', 'chaves', empresaIds],
    queryFn: ({ signal }) =>
      request('/chaves-pendentes', {
        schema: pagina(chavePendenteSchema),
        query: { empresaIds, status: 'PENDENTE', limit: 6 },
        signal,
      }),
  });
}

/** Contadores de pendências do mês (o painel completo fica em /pendencias). */
export function useResumoPendenciasDoMes(empresaIds: readonly string[]) {
  const { de, ate } = mesAtual();
  return useQuery({
    queryKey: ['pendencias', 'resumo', empresaIds, { emitidaDe: de, emitidaAte: ate }],
    queryFn: ({ signal }) =>
      request('/pendencias/resumo', {
        schema: resumoPendenciasSchema,
        query: { empresaIds, emitidaDe: de, emitidaAte: ate },
        signal,
      }),
  });
}

/** Empresas do escopo (dados públicos, com o vencimento do certificado) — saúde dos A1. */
export function useEmpresasDoEscopo(empresaIds: readonly string[]) {
  return useQuery({
    queryKey: ['empresas', 'inicio', empresaIds],
    queryFn: async ({ signal }) => {
      const todas = await request('/empresas', { schema: z.array(empresaResumoSchema), signal });
      return empresaIds.length === 0 ? todas : todas.filter((e) => empresaIds.includes(e.id));
    },
  });
}
